import {
  LearnStripItem,
  LearnStripSlice,
} from '@adapters/prismic/types/brandedStorePage';
import { LearnStripCard, LearnStripData } from '_components/LearnStrip/types';

const toCard = (item: LearnStripItem): LearnStripCard | null => {
  const heading = item.heading?.trim();
  const ctaLabel = item.cta_text?.trim();
  const url = item.url?.trim();
  const imageUrl = item.image?.url;

  // A card with no destination, no label, no heading or no photo has nothing
  // to render, and a gap in the strip reads as a bug rather than as missing
  // content. Dropping it here keeps the guard at the boundary and lets the
  // component trust every card it is handed.
  if (!heading || !ctaLabel || !url || !imageUrl) return null;

  const subheading = item.subheading?.trim();

  return {
    heading,
    ...(subheading ? { subheading } : {}),
    cta: { label: ctaLabel, url },
    image: {
      url: imageUrl,
      // Left empty rather than filled from the heading. The heading is already
      // rendered and already in the link's accessible name, so repeating it
      // here would announce the card's title twice; an unauthored alt is
      // better treated as decorative.
      alt: item.image?.alt ?? '',
    },
  };
};

/**
 * Maps a Prismic `learn_strip` slice into the shape `_components/LearnStrip`
 * consumes. Shared by CategoryPageSliceHandler and LandingPageSliceHandler,
 * since the slice has the identical field shape on both custom types.
 *
 * Takes the two slice zones rather than the slice, matching mapMainHeroData.
 * The landing page's slice union includes a catch-all whose `slice_type` is a
 * plain string, so a whole-slice parameter would not narrow at that call site.
 */
const mapLearnStripData = (
  primary: LearnStripSlice['primary'] | undefined,
  items: LearnStripItem[] | undefined
): LearnStripData => ({
  heading: primary?.title?.trim() ?? '',
  cards: (items ?? [])
    .map(toCard)
    .filter((card): card is LearnStripCard => card !== null),
});

export default mapLearnStripData;
